import "./Login.css"
import { useState, useEffect } from "react"
import { LuUser, LuLock } from "react-icons/lu"
import { useNavigate } from "react-router-dom"
import { auth, db } from "../firebase"
import {
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup
} from "firebase/auth"
import { doc, setDoc, getDoc } from "firebase/firestore"

function Login() {
  const [imgLoaded, setImgLoaded] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [alert, setAlert] = useState(null)

  const navigate = useNavigate()
  const provider = new GoogleAuthProvider()

  const showAlert = (message, type = "info") => {
    setAlert({ message, type })
    setTimeout(() => setAlert(null), 3000)
  }

  const clearAlert = () => setAlert(null)

  const getFriendlyError = (code) => {
    switch (code) {
      case "auth/user-not-found":
      case "auth/wrong-password":
      case "auth/invalid-credential":
        return "Invalid email or password"
      case "auth/invalid-email":
        return "Invalid email format"
      case "auth/popup-closed-by-user":
        return "Google popup closed"
      case "auth/too-many-requests":
        return "Too many attempts. Try later"
      default:
        return "Login failed. Try again"
    }
  }

  useEffect(() => {
    window.scrollTo(0, 0)
    const timer = setTimeout(() => setLoaded(true), 100)
    return () => clearTimeout(timer)
  }, [])

  const handleLogin = async () => {
    if (!email || !password) {
      showAlert("Fill all fields", "error")
      return
    }

    setIsLoading(true)

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      )

      const user = userCredential.user
      await user.reload()

      if (!user.emailVerified) {
        showAlert("Email not verified", "info")
        setTimeout(() => navigate("/verify-email"), 800)
        return
      }

      showAlert("Welcome back!", "success")
      setTimeout(() => navigate("/dashboard"), 600)

    } catch (error) {
      showAlert(getFriendlyError(error.code), "error")
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoogleLogin = async () => {
    setIsLoading(true)

    try {
      const result = await signInWithPopup(auth, provider)
      const user = result.user
      const userRef = doc(db, "users", user.uid)
      const snap = await getDoc(userRef)

      if (!snap.exists()) {
        await setDoc(userRef, {
          name: user.displayName || user.email.split("@")[0],
          email: user.email,
          branch: "",
          year: "",
          college: "",
          careerGoal: "",
          skillLevel: "",
          weeklyCommitment: "",
          screentime: {},
          informationProvided: false,
          createdAt: new Date()
        })
      }

      showAlert("Welcome!", "success")
      setTimeout(() => navigate("/dashboard"), 600)

    } catch (error) {
      showAlert(getFriendlyError(error.code), "error")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={`login-wrapper ${loaded ? "loaded" : ""}`}>
      <div className="login-container">

        <div className="login-right">
          {!imgLoaded && <div className="image-skeleton"></div>}
          <img
            src="/assets/login.png"
            alt="illustration"
            className={`login-image ${imgLoaded ? "show" : "hide"}`}
            onLoad={() => setImgLoaded(true)}
          />
        </div>

        <div className="login-left">
          <div className="login-content">

            <h1>Hello Again!</h1>
            <p className="login-subtitle">
              Let’s get started with your personalized career roadmap.
            </p>

            {alert && (
              <div className={`alert ${alert.type}`}>
                <span className="alert-icon">
                  {alert.type === "success" ? "✓" : alert.type === "error" ? "✕" : "ℹ"}
                </span>
                <span>{alert.message}</span>
                <button className="alert-close" onClick={clearAlert}>
                  ×
                </button>
              </div>
            )}

            <div className="input-group">
              <LuUser className="input-icon" />
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
            </div>

            <div className="input-group">
              <LuLock className="input-icon" />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={e => setPassword(e.target.value)}
              />
            </div>

            <div className="recovery">Recovery Password</div>

            <button
              className="login-btn"
              onClick={handleLogin}
              disabled={isLoading}
            >
              <div className="btn-with-loader">
                {isLoading && <div className="spinner"></div>}
                {isLoading ? "Signing In..." : "Sign In"}
              </div>
            </button>

            <div className="signup-redirect">
              Don’t have an account?
              <span onClick={() => navigate("/signup")}>
                {" "}Create one
              </span>
            </div>

            <div className="divider">
              <span>Or continue with</span>
            </div>

            <div className="social-row">
              <button
                className="social-icon-btn google"
                onClick={handleGoogleLogin}
                disabled={isLoading}
              >
                <img
                  src="https://developers.google.com/identity/images/g-logo.png"
                  alt="Google"
                  className="google-logo"
                />
              </button>

              <button className="social-icon-btn apple">
                <img
                  src="https://www.apple.com/ac/structured-data/images/knowledge_graph_logo.png"
                  alt="Apple"
                  className="apple-logo"
                />
              </button>
            </div>

          </div>
        </div>

      </div>
    </div>
  )
}

export default Login